/**
 * Messaging roles on the client — mirrors messaging-gateway `access/tenant-role.ts`, which is
 * what actually enforces them. Used to hide what a role can't do; never as the security boundary.
 *
 * - Membership role per tenant comes from SSO userinfo `tenants[].role` (missing → member).
 * - Global admin comes only from the SSO `global_admin` claim (SSO owns which roles count).
 */
export type TenantRole = 'owner' | 'admin' | 'member' | 'viewer';
export type EffectiveRole = 'admin' | 'member' | 'viewer';
export type Capability =
  | 'reply'
  | 'manageConversation'
  | 'manageSharedCanned'
  | 'manageTags'
  | 'viewReports'
  | 'manageAppearance'
  | 'reassign';

export interface Access {
  isGlobalAdmin: boolean;
  roles: Record<string, TenantRole>;
}

const TENANT_ROLES = new Set<string>(['owner', 'admin', 'member', 'viewer']);
const RANK: Record<EffectiveRole, number> = { viewer: 1, member: 2, admin: 3 };
const NEEDS: Record<Capability, EffectiveRole> = {
  reply: 'member',
  manageConversation: 'member',
  manageSharedCanned: 'admin',
  manageTags: 'admin',
  viewReports: 'admin',
  manageAppearance: 'admin', // the workspace look — gateway PUT /api/appearance/tenants/:id
  reassign: 'admin', // give a conversation to someone / release theirs — gateway `conversation.reassign`
};

export function accessFromUser(user: unknown): Access {
  const u = (user ?? {}) as { tenants?: unknown; global_admin?: unknown };
  const roles: Record<string, TenantRole> = {};
  if (Array.isArray(u.tenants)) {
    for (const t of u.tenants as Array<{ id?: unknown; role?: unknown }>) {
      if (!t || t.id == null) continue;
      const r = typeof t.role === 'string' && TENANT_ROLES.has(t.role) ? (t.role as TenantRole) : 'member';
      roles[String(t.id)] = r;
    }
  }
  return { isGlobalAdmin: u.global_admin === true, roles };
}

export function roleIn(access: Access, tenantId: string | null | undefined): EffectiveRole | null {
  if (access.isGlobalAdmin) return 'admin';
  const r = tenantId ? access.roles[tenantId] : undefined;
  if (!r) return null;
  return r === 'owner' ? 'admin' : r;
}

export function can(access: Access, capability: Capability, tenantId: string | null | undefined): boolean {
  if (!tenantId) return false;
  const role = roleIn(access, tenantId);
  return !!role && RANK[role] >= RANK[NEEDS[capability]];
}

export type RoleBadge = 'Global admin' | 'Owner' | 'Admin' | 'Member' | 'Viewer';

const BADGE: Record<TenantRole, RoleBadge> = { owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer' };

/** i18n key for each badge; the badge itself stays English (tests and logs use it). */
export const ROLE_BADGE_KEY = {
  'Global admin': 'roles.globalAdmin',
  Owner: 'roles.owner',
  Admin: 'roles.admin',
  Member: 'roles.member',
  Viewer: 'roles.viewer',
} as const satisfies Record<RoleBadge, string>;

export function roleBadge(access: Access, tenantId: string | null | undefined): RoleBadge | null {
  if (access.isGlobalAdmin) return 'Global admin';
  const r = tenantId ? access.roles[tenantId] : undefined;
  return r ? BADGE[r] : null;
}
